import scorecardDataValidation from "@components/ScorecardDataValidation/ScorecardDataValidation";
import {
  ScorecardObject,
  UploadPayload,
  useWebApiV1CreateAttachmentMutation,
  useWebApiV1CreateWalkthroughAttachmentUploadsMutation,
  useWebApiV1DeleteAttachmentMutation,
  useWebApiV1DeleteWalkthroughAttachmentMutation,
  useWebApiV1ListAttachmentsQuery,
  useWebApiV1ListWalkthroughAttachmentsQuery,
  useWebApiV1UpdateWalkthroughAttachmentMutation,
} from "@providers/hop-ord-server/api";
import {
  validateImage,
  validateScorecard,
  validateVideo,
  getFileExtensionValidationError,
  getFileSizeValidationError,
} from "@utils/fileValidation";
import { uploadToS3 } from "@utils/s3";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ErrorCode, FileRejection } from "react-dropzone";
import countBy from "lodash/countBy";
import { MAX_FILE_SIZE } from "@components/Dropzone/Dropzone";

export type AttachmentCategory =
  | "scorecard"
  | "walkthrough"
  | "image"
  | "other";

type BaseAtt<T extends AttachmentCategory> = {
  category: T;
  name: string;
  size: number;
  lastModified: Dayjs;
};

// state-specific fields
type BasePending<T extends AttachmentCategory> = BaseAtt<T> & {
  state: "pending";
  file: File;
};

type BaseRejected<T extends AttachmentCategory> = BaseAtt<T> & {
  state: "rejected";
  reasons: string[];
};

type BaseUploading<T extends AttachmentCategory> = BaseAtt<T> & {
  state: "uploading";
  id: number;
  file: File;
  progress: number;
  presignedPost: UploadPayload;
  createdAt: Dayjs;
  toDelete: boolean;
};

type BaseCompleted<T extends AttachmentCategory> = BaseAtt<T> & {
  state: "completed";
  id: number;
  createdAt: Dayjs;
  sentForReview: boolean;
  toDelete: boolean;
};

type BaseError<T extends AttachmentCategory> = BaseAtt<T> & {
  state: "error";
  id: number;
  message: string;
  code: string;
};

// scorecard specific fields
type BaseScorecard = {
  scorecard: ScorecardObject;
};

// completed media specific fields
type BaseCompletedMedia = {
  url: string;
};

// scorecard types
export type PendingScorecard = BasePending<"scorecard"> & BaseScorecard;
type RejectedScorecard = BaseRejected<"scorecard">;
export type CompletedScorecard = BaseCompleted<"scorecard"> &
  BaseScorecard & { sentForReview: true }; // completed scorecards are always sent for review
type ErrorScorecard = BaseError<"scorecard">;

// walkthrough types
type PendingWalkthrough = BasePending<"walkthrough">;
type RejectedWalkthrough = BaseRejected<"walkthrough">;
export type UploadingWalkthrough = BaseUploading<"walkthrough">;
export type CompletedWalkthrough = BaseCompleted<"walkthrough"> &
  BaseCompletedMedia;
type ErrorWalkthrough = BaseError<"walkthrough">;

// image types
type PendingImage = BasePending<"image">;
type RejectedImage = BaseRejected<"image">;
type UploadingImage = BaseUploading<"image">;
export type CompletedImage = BaseCompleted<"image"> & BaseCompletedMedia;
type ErrorImage = BaseError<"image">;

// other (rejected only)
export type RejectedOther = BaseRejected<"other">;

// kind specific unions
export type Scorecard =
  | PendingScorecard
  | RejectedScorecard
  | CompletedScorecard
  | ErrorScorecard;

export type Walkthrough =
  | PendingWalkthrough
  | RejectedWalkthrough
  | UploadingWalkthrough
  | CompletedWalkthrough
  | ErrorWalkthrough;

export type Image =
  | PendingImage
  | RejectedImage
  | UploadingImage
  | CompletedImage
  | ErrorImage;

// one type to track all the files, no matter what kind or state
export type Attachment = Scorecard | Walkthrough | Image | RejectedOther;

// utility unions
type RejectedAtt =
  | RejectedScorecard
  | RejectedWalkthrough
  | RejectedImage
  | RejectedOther;

type ErrorAtt = ErrorScorecard | ErrorWalkthrough | ErrorImage;

type Pending = PendingScorecard | PendingWalkthrough | PendingImage;
type PendingMedia = PendingWalkthrough | PendingImage;
type UploadingMedia = UploadingWalkthrough | UploadingImage;
export type CompletedMedia = CompletedWalkthrough | CompletedImage;
export type ValidImage = PendingImage | UploadingImage | CompletedImage;
export type ValidWalkthrough =
  | PendingWalkthrough
  | UploadingWalkthrough
  | CompletedWalkthrough;

// type helpers
const isPending = (att: Attachment): att is Pending => att.state === "pending";

export const isCompleted = (
  att: Attachment,
): att is CompletedScorecard | CompletedMedia => att.state === "completed";

export const isUploading = (att: Attachment): att is UploadingMedia =>
  att.state === "uploading";

export const isMedia = (att: Attachment): att is Image | Walkthrough =>
  att.category === "walkthrough" || att.category === "image";

export const isWalkthrough = (att: Attachment): att is Walkthrough =>
  att.category === "walkthrough";

export const isImage = (att: Attachment): att is Image =>
  att.category === "image";

export const isScorecard = (att: Attachment): att is Scorecard =>
  att.category === "scorecard";

export const hasFile = (att: Attachment): att is Pending | UploadingMedia =>
  "file" in att;

export const isValid = (
  att: Attachment,
): att is Exclude<Attachment, RejectedAtt | ErrorAtt> =>
  att.state !== "rejected" && att.state !== "error";

export const isNotDeleted = (att: Attachment) => {
  if ("toDelete" in att) {
    return !att.toDelete;
  } else {
    return true;
  }
};

const JSON_FILE_EXTS = ["json"];
const VIDEO_FILE_EXTS = ["mov", "mp4"];
const IMAGE_FILE_EXTS = ["png", "jpg", "jpeg"];

const hasExtension = (fileName: string, extensions: string[]) => {
  const ext = fileName.split(".").at(-1)?.toLowerCase();
  return extensions.includes(ext ?? "");
};

type KindCounts = {
  scorecards: number;
  walkthroughs: number;
};

export type AttachmentMap = ReadonlyMap<string, Attachment>;

// this is a very silly hook
const useAttachmentMap = () => {
  const [attachmentMap, setAttachmentMap] = useState<AttachmentMap>(new Map());
  const [doRecount, setDoRecount] = useState(false);

  const keyRef = useRef<number>(0);
  const getLocalKey = useCallback(() => `local-${keyRef.current++}`, []);

  // Server keys are prefixed with the kind because scorecards and media
  // attachments come from different tables, and can have overlapping ids.
  const getServerKey = useCallback(
    (id: number, kind: AttachmentCategory) => `${kind}-${id}`,
    [],
  );

  // Add local attachment.
  const addLocalAtt = useCallback((att: Attachment) => {
    const newKey = getLocalKey();
    setAttachmentMap((prev) => new Map(prev).set(newKey, att));
    setDoRecount(true);
    return newKey;
  }, []);

  type ServerAtt = CompletedMedia | CompletedScorecard | UploadingMedia;

  // Adds a server attachment if it's not in the map already, or updates it if the
  // predicate is true.
  const setServerAttIf = useCallback(
    (att: ServerAtt, updateIf: (prevAtt: Attachment) => boolean) => {
      const key = getServerKey(att.id, att.category);
      setAttachmentMap((prev) => {
        const prevAtt = prev.get(key);
        if (prevAtt === undefined || updateIf(prevAtt)) {
          setDoRecount(true);
          return new Map(prev).set(key, att);
        }
        return prev;
      });
    },
    [],
  );

  // This function does not trigger a recount.
  // No-op if the attachment isn't the map, or has the wrong state.
  const updateUploadProgress = useCallback((key: string, progress: number) => {
    setAttachmentMap((prev) => {
      const prevAtt = prev.get(key);
      if (prevAtt === undefined || prevAtt.state !== "uploading") {
        // don't know when this will be called, safe to ignore if invalid
        return prev;
      }
      return new Map(prev).set(key, { ...prevAtt, progress });
    });
  }, []);

  const markForDeletion = useCallback((key: string) => {
    setAttachmentMap((prev) => {
      const prevAtt = prev.get(key);
      if (
        prevAtt !== undefined &&
        (isCompleted(prevAtt) || isUploading(prevAtt))
      ) {
        return new Map(prev).set(key, { ...prevAtt, toDelete: true });
      }
      return prev;
    });
    setDoRecount(true);
  }, []);

  // No-op if the attachment is not in the map.
  const updateAtt = useCallback((key: string, att: Attachment) => {
    setAttachmentMap((prev) => {
      const hasKey = prev.has(key);
      setDoRecount(hasKey);
      return hasKey ? new Map(prev).set(key, att) : prev;
    });
  }, []);

  // Updates and rekeys a attachment while preserving order of the map.
  const updateAndRekeyAtt = useCallback((oldKey: string, att: ServerAtt) => {
    const newKey = getServerKey(att.id, att.category);
    setAttachmentMap((prev) => {
      return new Map(
        Array.from(prev.entries()).map(([k, v]) => {
          return k === oldKey ? ([newKey, att] as const) : ([k, v] as const);
        }),
      );
    });
    setDoRecount(true);
    return newKey;
  }, []);

  const removeAtt = useCallback((key: string) => {
    setAttachmentMap((prev) => {
      const newMap = new Map(prev);
      newMap.delete(key);
      return newMap;
    });
    setDoRecount(true);
  }, []);

  const resetMap = useCallback(() => {
    setAttachmentMap(new Map());
    setDoRecount(true);
  }, []);

  const [acceptedAttCounts, setAcceptedAttCounts] = useState<KindCounts>({
    scorecards: 0,
    walkthroughs: 0,
  });

  // full recount whenever the attachments change
  useEffect(() => {
    if (!doRecount) {
      return;
    }
    const counts = countBy(
      Array.from(attachmentMap.values())
        .filter(({ state }) => state !== "rejected")
        .filter(isNotDeleted),
      ({ category: kind }) => kind,
    );
    setAcceptedAttCounts((prev) => {
      const next = {
        scorecards: counts["scorecard"] ?? 0,
        walkthroughs: counts["walkthrough"] ?? 0,
      };
      return prev.scorecards === next.scorecards &&
        prev.walkthroughs === next.walkthroughs
        ? prev
        : next;
    });
    setDoRecount(false);
  }, [attachmentMap, doRecount]);

  return {
    attachmentMap,
    acceptedAttCounts,
    removeAtt,
    updateAtt,
    addLocalAtt,
    setServerAttIf,
    updateAndRekeyAtt,
    updateUploadProgress,
    markForDeletion,
    resetMap,
  };
};

const useServerAttachments = (orderId: number) => {
  const {
    data: scorecardsData,
    isFetching: scorecardsFetching,
    refetch: refetchScorecards,
  } = useWebApiV1ListAttachmentsQuery({ orderId });

  const {
    data: mediaData,
    isFetching: mediaFetching,
    refetch: refetchMedia,
  } = useWebApiV1ListWalkthroughAttachmentsQuery({ orderId });

  const refetchServerAttachments = useCallback(async () => {
    return await Promise.all([refetchScorecards(), refetchMedia()]);
  }, [refetchMedia, refetchScorecards]);

  const queriesLoading = scorecardsFetching || mediaFetching;

  const serverAttachments = useMemo(() => {
    const scorecards =
      scorecardsData?.scorecards?.map(
        (scorecard): CompletedScorecard => ({
          id: scorecard.id,
          category: "scorecard",
          state: "completed",
          name: scorecard.fileName,
          sentForReview: true,
          createdAt: dayjs(scorecard.createdAt),
          lastModified: dayjs(scorecard.lastModifiedDate),
          size: scorecard.scorecard?.length ?? 0,
          toDelete: false,
          scorecard: scorecard.scorecardObj,
        }),
      ) ?? [];
    const media =
      mediaData?.map((media): CompletedMedia | null => {
        // The server doesn't tell us if the attachment is is completed or
        // uploading, so we default to completed. When we put these in the map,
        // we only update the attachment if the map thinks it's completed too.
        let category: "walkthrough" | "image";
        if (hasExtension(media.fileName, VIDEO_FILE_EXTS)) {
          category = "walkthrough";
        } else if (hasExtension(media.fileName, IMAGE_FILE_EXTS)) {
          category = "image";
        } else {
          console.error(`Unknown file type for file: ${media.fileName}`);
          return null;
        }

        return {
          id: media.id,
          category: category,
          state: "completed",
          name: media.fileName,
          sentForReview: media.sentForReview,
          createdAt: dayjs(media.createdAt),
          lastModified: dayjs(media.lastModifiedDate),
          size: media.fileSize,
          toDelete: false,
          url: media.file,
        };
      }) ?? [];

    // their order only matters if they were existing completed attachments
    return [...scorecards, ...media.filter((m) => m !== null)].sort((a, b) =>
      a.createdAt.isBefore(b.createdAt) ? 1 : -1,
    );
  }, [scorecardsData, mediaData]);
  return { queriesLoading, serverAttachments, refetchServerAttachments };
};

const jsonFileToScorecard = async (
  file: File,
): Promise<ScorecardObject | string> => {
  try {
    const fileContent = await file.text();
    const scorecard = JSON.parse(fileContent);
    if (typeof scorecard !== "object") {
      throw new SyntaxError();
    }
    if (scorecardDataValidation(scorecard)) {
      return scorecard;
    } else {
      return "File has missing or invalid fields";
    }
  } catch (error) {
    if (error instanceof SyntaxError) {
      return "File is not valid JSON";
    }
    return `Something went wrong (${error})`;
  }
};

const createRejectedAttachment = <T extends AttachmentCategory>(
  kind: T,
  file: File,
  reasons: string[],
): BaseRejected<T> => {
  return {
    category: kind,
    state: "rejected",
    name: file.name,
    reasons,
    size: file.size,
    lastModified: dayjs(file.lastModified),
  };
};

/** Creates a whole bunch of PendingAtt or RejectedAtt. */
const validateFiles = async (files: File[], fileCounts: KindCounts) => {
  return await Promise.all(
    files.map(async (file): Promise<Pending | RejectedAtt> => {
      // scorecards
      if (hasExtension(file.name, JSON_FILE_EXTS)) {
        // counts
        if (fileCounts.scorecards > 1) {
          return createRejectedAttachment("scorecard", file, [
            "Too many scorecards",
          ]);
        }
        // scan
        const scanResult = await validateScorecard(file);
        if (scanResult.errors.length > 0) {
          return createRejectedAttachment("scorecard", file, scanResult.errors);
        }
        // validate json/scorecard object
        const scorecardResult = await jsonFileToScorecard(file);
        if (typeof scorecardResult === "string") {
          return createRejectedAttachment("scorecard", file, [scorecardResult]);
        }
        // create
        return {
          category: "scorecard",
          state: "pending",
          name: file.name,
          file,
          scorecard: scorecardResult,
          size: file.size,
          lastModified: dayjs(file.lastModified),
        };
      }
      // walkthroughs
      if (hasExtension(file.name, VIDEO_FILE_EXTS)) {
        // counts
        if (fileCounts.walkthroughs > 1) {
          return createRejectedAttachment("walkthrough", file, [
            "Too many walkthroughs",
          ]);
        }
        // scan
        const scanResult = await validateVideo(file);
        if (scanResult.errors.length > 0) {
          return createRejectedAttachment(
            "walkthrough",
            file,
            scanResult.errors,
          );
        }
        // create
        return {
          category: "walkthrough",
          name: file.name,
          state: "pending",
          file,
          size: file.size,
          lastModified: dayjs(file.lastModified),
        };
      }
      // images
      if (hasExtension(file.name, IMAGE_FILE_EXTS)) {
        // scan
        const scanResult = await validateImage(file);
        if (!scanResult.isValid) {
          return createRejectedAttachment("image", file, scanResult.errors);
        }
        // create
        return {
          category: "image",
          name: file.name,
          state: "pending",
          file,
          size: file.size,
          lastModified: dayjs(file.lastModified),
        };
      }
      return createRejectedAttachment("other", file, ["Unknown file type"]);
    }),
  );
};

const useSubmitAttachments = (orderId: number) => {
  const [submitScorecard] = useWebApiV1CreateAttachmentMutation();
  const [updateMedia] = useWebApiV1UpdateWalkthroughAttachmentMutation();
  return useCallback(
    async (attachmentMap: AttachmentMap) => {
      const scorecards = Array.from(attachmentMap.values())
        .filter((att) => {
          return isScorecard(att) && isPending(att);
        })
        .map(async (att) => {
          return await submitScorecard({
            orderId,
            scorecardAttachmentIn: {
              scorecard: att.scorecard,
              fileName: att.name,
            },
          }).unwrap();
        });

      const values = Array.from(attachmentMap.values());
      const media = values
        .filter((att) => isMedia(att) && isCompleted(att))
        .filter((att) => !att.sentForReview)
        .map(async (att) => {
          return await updateMedia({
            orderId,
            fileId: att.id,
            uploadComplete: true,
            sentForReview: true,
            read: false,
          }).unwrap();
        });

      return await Promise.all([...scorecards, ...media]);
    },
    [orderId, submitScorecard, updateMedia],
  );
};

const useDeleteAttachments = (orderId: number) => {
  const [deleteScorecardAttachment] = useWebApiV1DeleteAttachmentMutation();
  const [deleteMediaAttachment] =
    useWebApiV1DeleteWalkthroughAttachmentMutation();
  return useCallback(
    async (attachments: AttachmentMap) => {
      const values = Array.from(attachments.values());
      return await Promise.all(
        values
          .filter((att) => isCompleted(att) || isUploading(att))
          .filter((att) => att.toDelete)
          .map(async (att) => {
            if (isMedia(att)) {
              return deleteMediaAttachment({ orderId, attachmentId: att.id });
            } else {
              return deleteScorecardAttachment({
                orderId,
                attachmentId: att.id,
              });
            }
          }),
      );
    },
    [orderId, deleteScorecardAttachment, deleteMediaAttachment],
  );
};

const useRegisterMediaAttachments = () => {
  const [registerMediaAttachments] =
    useWebApiV1CreateWalkthroughAttachmentUploadsMutation();
  return useCallback(
    async (orderId: number, mediaEntries: [string, PendingMedia][]) => {
      // submit the attachments to LP
      const { data: submissionResult, error: submissionError } =
        await registerMediaAttachments({
          orderId,
          fileNames: mediaEntries.map(([_, att]) => att.name),
        });

      if (submissionError) {
        throw submissionError;
      }
      // map the presigned urls to the attachments to turn them into
      // UploadingMediaAtt
      return submissionResult.map(
        (result, index): { oldKey: string; att: UploadingMedia } => {
          const [oldKey, att] = mediaEntries[index];
          return {
            oldKey,
            att: {
              category: att.category,
              state: "uploading",
              id: result.id,
              name: att.name,
              file: att.file,
              size: att.file.size,
              lastModified: dayjs(att.file.lastModified),
              progress: 0,
              presignedPost: result.uploadUrl,
              createdAt: dayjs(),
              toDelete: false,
            },
          };
        },
      );
    },
    [registerMediaAttachments],
  );
};

const useUploadMediaAttachments = () => {
  const [updateMedia] = useWebApiV1UpdateWalkthroughAttachmentMutation();
  return useCallback(
    async (
      orderId: number,
      mediaFileEntries: [string, UploadingMedia][],
      updateUploadProgress: (key: string, progress: number) => void,
      updateAtt: (key: string, att: Attachment) => void,
    ) => {
      // upload the files to S3
      await Promise.all(
        mediaFileEntries.map(async ([key, att]) => {
          uploadToS3({
            payload: att.presignedPost,
            file: att.file,
            setProgress: (progress) => updateUploadProgress(key, progress),
            onComplete: async () => {
              try {
                const result = await updateMedia({
                  orderId,
                  fileId: att.id,
                  uploadComplete: true,
                  sentForReview: false,
                  read: false,
                }).unwrap();

                updateAtt(key, {
                  id: result.id,
                  category: att.category,
                  state: "completed",
                  name: result.fileName,
                  createdAt: dayjs(result.createdAt),
                  sentForReview: result.sentForReview,
                  lastModified: dayjs(result.lastModifiedDate),
                  size: result.fileSize,
                  toDelete: false,
                  url: result.file,
                });
              } catch (_error) {
                updateAtt(key, {
                  category: att.category,
                  state: "error",
                  name: att.name,
                  id: att.id,
                  message: "Upload failed",
                  code: "upload-failed",
                  size: att.file.size,
                  lastModified: dayjs(att.lastModified),
                });
              }
            },
            onError: () => {
              updateAtt(key, {
                category: att.category,
                id: att.id,
                state: "error",
                name: att.name,
                message: "Upload failed",
                code: "upload-failed",
                size: att.file.size,
                lastModified: att.lastModified,
              });
            },
          });
        }),
      );
    },
    [updateMedia],
  );
};

type UseFileMapReturn = ReturnType<typeof useAttachmentMap>;
interface UseDropHandlerProps {
  orderId: number;
  acceptedAttCounts: KindCounts;
  updateAtt: UseFileMapReturn["updateAtt"];
  addLocalAtt: UseFileMapReturn["addLocalAtt"];
  updateAndRekeyAtt: UseFileMapReturn["updateAndRekeyAtt"];
  updateUploadProgress: UseFileMapReturn["updateUploadProgress"];
}

const useDropHandler = ({
  orderId,
  acceptedAttCounts,
  addLocalAtt,
  updateAtt,
  updateAndRekeyAtt,
  updateUploadProgress,
}: UseDropHandlerProps) => {
  const createMediaAttachments = useRegisterMediaAttachments();
  const uploadMediaAttachments = useUploadMediaAttachments();

  return useCallback(
    async (accepted: File[], rejected: FileRejection[]) => {
      // count scorecards and walkthroughs
      const counts = accepted.reduce<KindCounts>((acc, file) => {
        if (hasExtension(file.name, JSON_FILE_EXTS)) {
          return { ...acc, scorecards: acc.scorecards + 1 };
        }
        if (hasExtension(file.name, VIDEO_FILE_EXTS)) {
          return { ...acc, walkthroughs: acc.walkthroughs + 1 };
        }
        return acc;
      }, acceptedAttCounts);

      // validate and map to array of PendingAtt or RejectedAtt
      const validationResults = await validateFiles(accepted, counts);

      // map all the rejected ones (there might not be any?)
      // these will get appended to the end which is a bit weird.
      // TODO: is this needed?
      const validatedAttachments = [
        ...validationResults,
        ...rejected.map((rejection) =>
          createRejectedAttachment(
            "other",
            rejection.file,
            rejection.errors.map(({ code }) => {
              switch (code) {
                case ErrorCode.FileTooLarge:
                  return getFileSizeValidationError(
                    rejection.file.size,
                    MAX_FILE_SIZE,
                  );
                case ErrorCode.FileInvalidType:
                  return getFileExtensionValidationError(
                    rejection.file.name.split(".").pop() ?? "",
                  );
                default:
                  return `Unhandled error (${code})`;
              }
            }),
          ),
        ),
      ];

      const pendingMediaEntries: [string, PendingMedia][] = [];

      // add to the file map
      for (const att of validatedAttachments) {
        const newKey = addLocalAtt(att);
        if (isPending(att) && isMedia(att)) {
          pendingMediaEntries.push([newKey, att]);
        }
      }

      if (pendingMediaEntries.length > 0) {
        // submit the media files
        const submittedMediaAttachments = await createMediaAttachments(
          orderId,
          pendingMediaEntries,
        );

        // rekey the sumbitted attachments with their new server keys
        const rekeyedMediaFiles = submittedMediaAttachments.map(
          ({ oldKey, att }): [string, UploadingMedia] => {
            const newKey = updateAndRekeyAtt(oldKey, att);
            return [newKey, att];
          },
        );

        // upload the media attachments
        await uploadMediaAttachments(
          orderId,
          rekeyedMediaFiles,
          updateUploadProgress,
          updateAtt,
        );
      }
    },
    [
      orderId,
      acceptedAttCounts,
      createMediaAttachments,
      uploadMediaAttachments,
      updateUploadProgress,
      updateAtt,
      updateAndRekeyAtt,
      addLocalAtt,
    ],
  );
};

const useManageAttachments = (orderId: number) => {
  // setup our file map to preserve ordering
  const {
    attachmentMap,
    acceptedAttCounts,
    updateAtt,
    removeAtt,
    addLocalAtt,
    setServerAttIf,
    updateAndRekeyAtt,
    updateUploadProgress,
    markForDeletion,
    resetMap,
  } = useAttachmentMap();

  // get the existing files from the server
  const { queriesLoading, serverAttachments, refetchServerAttachments } =
    useServerAttachments(orderId);

  // update or add the existing files to the file map
  useEffect(() => {
    if (queriesLoading) {
      return;
    }
    // we only want to add/update files that are completed and not marked for
    // deletion (because we don't want to overwrite them otherwise)
    serverAttachments.forEach((file) => {
      setServerAttIf(file, (att) => att.state === "completed" && !att.toDelete);
    });
  }, [serverAttachments, queriesLoading, setServerAttIf]);

  const isSyncedWithServer = useMemo(() => {
    if (queriesLoading) {
      return false;
    }
    let synced = true;
    for (const serverAtt of serverAttachments) {
      const att = Array.from(attachmentMap.values()).find((att) => {
        return (
          (isCompleted(att) || isUploading(att)) &&
          att.id === serverAtt.id &&
          att.category === serverAtt.category
        );
      });
      if (!att) {
        synced = false;
        break;
      }
    }
    return synced;
  }, [queriesLoading, serverAttachments, attachmentMap]);

  // create the drop handler
  const handleDrop = useDropHandler({
    orderId,
    acceptedAttCounts,
    updateAtt,
    addLocalAtt,
    updateAndRekeyAtt,
    updateUploadProgress,
  });

  const [deleteMediaAttachment] =
    useWebApiV1DeleteWalkthroughAttachmentMutation();
  const deleteAttachment = useCallback(
    (key: string, att: Attachment) => {
      if (isCompleted(att) && att.sentForReview) {
        // mark as deleted
        markForDeletion(key);
      } else if (isUploading(att) || isCompleted(att)) {
        // remove from the map, then delete from the server
        removeAtt(key);
        deleteMediaAttachment({ orderId, attachmentId: att.id });
      } else {
        // att is pending, rejected or error
        removeAtt(key);
      }
    },
    [markForDeletion, removeAtt, deleteMediaAttachment, orderId],
  );

  const submitAttachments = useSubmitAttachments(orderId);
  const deleteAttachments = useDeleteAttachments(orderId);
  const [committingAttachments, setCommittingAttachments] = useState(false);

  const commitAttachments = useCallback(
    async (attachments: AttachmentMap) => {
      setCommittingAttachments(true);
      await submitAttachments(attachments);
      await deleteAttachments(attachments);
      resetMap();
      setCommittingAttachments(false);
      await refetchServerAttachments();
    },
    [submitAttachments, deleteAttachments, refetchServerAttachments, resetMap],
  );

  return {
    busy: committingAttachments || !isSyncedWithServer,
    attachmentMap,
    deleteAttachment,
    commitAttachments,
    // handleDrop can't be used until acceptedAttCounts has caught up.
    // or it should cache the call to handleDrop until the server is synced
    handleDrop: isSyncedWithServer ? handleDrop : null,
  };
};

export default useManageAttachments;
